import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Typography from '@material-ui/core/Typography';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

import VaporExtension from 'app/utils/VaporExtension';
import FileTypes from 'app/utils/FileTypes';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    input: {
        display: 'none',
    },
    overflowFix: {
        marginBottom: 0,
        marginTop: 0
    },
    spacing: {
        padding: theme.spacing(3),
    }
}));

const initValues = {
    id: 0,
    group_id: 0,
    folder_id: 0,
    name: ``,
}

export default function EditUserFileDialog(props) {

    const classes = useStyles();
    const api = useApi();

    const {
        initFile,
        open,
        onClose,
        onSave,
        onClosed,
    } = props;

    const visibility = props.visibility ?? 'public';

    const [ values, setValues ] = useState(initValues);
    const [ file, setFile ] = useState(null);
    const [ extension, setExtension ] = useState(null);

    const [ step, setStep ] = useState(1);

    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);
    const [ uploadProgress, setUploadProgress ] = useState(0);

    // Getter Functions -----------------------------------------------

    useEffect(() => {
        if (initFile) setValues(initFile);
        setErrors({});
    }, [initFile]);

    // Setter Functions -----------------------------------------------

    const handleFile = () => {
        if (isError('file')) delete errors.file;

        const newFile = document.getElementById('file-input').files[0];
        const extObject = FileTypes.find(obj => obj.mime == newFile.type);

        if (newFile.size > 10485760) {
            setErrors({file: 'The file size is too large. Please use files less than 10MB.'});
            return false;
        }

        if (! extObject) {
            setErrors({file: 'This file type is not supported.'});
            return false;
        }

        setFile(newFile);
        setExtension(extObject.ext);
        setValues({...values, name: newFile.name })
    }

    const handleFileName = e => {
        const value = e.target.value;

        if (isError('name')) delete errors.message;

        setValues({ ...values, name: value });
    };

    const handleStep = (e,s) => {
        e.preventDefault();
        setStep(s);
    };

    // Post Functions -----------------------------------------------

    const canPostFile = () => {
        if (loading) return false;
        if (step === 3) return false;
        if (values.id == 0 && !file) return false;

        return true;
    }

    const uploadFile = () => {

        if (file) {
            setStep(3);
            const token = api.defaults.headers.common['Authorization']

            VaporExtension.store(file, {
                progress: progress => setUploadProgress(Math.round(progress * 100))
            }, token).then(response => {
                api.post('/profile/file', {
                    uuid: response.uuid,
                    url: response.url,
                    key: response.key,
                    extension: extension,
                    bucket: response.bucket,
                    name: values.name,
                    content_type: file.type,
                    size: file.size,
                    visibility: visibility,
                }).then(res => {
                    onSave(res.file);
                }).catch(error => {
                    setStep(1);
                    setUploadProgress(0);

                    if (error?.response?.data?.errors) {
                        setErrors(error.response.data.errors)
                    }
                });
            }).catch(() => {
                setStep(1);
                setUploadProgress(0);
                setErrors({file: 'Unable to upload your file at this time. Please try again later.'})
            });
        }
    }

    const postFile = () => {
        api.post('/profile/file/' + values.id, values)
            .then(() => onSave())
            .catch(error => {
                setLoading(false);

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const onExited = () => {

        setValues({...initValues});

        setFile(null);
        setExtension(null);
        setStep(1);
        setLoading(false);

        if (onClosed) onClosed();
    }

    return (
        <Dialog
            aria-labelledby="edit-file-dialog-title"
            open={open}
            aria-describedby="edit-file-dialogue-description"
            fullWidth
            maxWidth="xs"
            disableBackdropClick={(step === 3 || loading)}
            disableEscapeKeyDown={(step === 3 || loading)}
            onClose={onClose}
            onExited={onExited}
            >
            <Loading isLoading={loading}/>
            {(step === 1) &&
                 <DialogTitle id="edit-file-dialog-title">
                    {(values.id > 0) ? "Editing " + values.name : "Upload a New File"}
                    <IconButton
                        aria-label="Cancel"
                        className={classes.closeButton}
                        onClick={onClose}
                        disabled={(step === 3 || loading)}
                        >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
            }
            <DialogContent>
                {(step === 1) &&
                    <Fragment>
                        {(! initFile) ? (
                            <Fragment>
                                <Grid
                                    container
                                    alignItems="center"
                                    spacing={3}
                                    className={classes.overflowFix}
                                    >
                                    <Grid item xs={false}>
                                        <input
                                            accept="*/*"
                                            className={classes.input}
                                            id="file-input"
                                            type="file"
                                            onChange={handleFile}
                                        />
                                        <label htmlFor="file-input">
                                            <Button component="span" color="primary" variant="contained">
                                                Open
                                            </Button>
                                        </label>
                                    </Grid>
                                    <Grid item xs={true}>
                                        <Typography variant="subtitle2">
                                            { file ? (
                                                <TextField
                                                    id="input-file-name"
                                                    value={values.name}
                                                    onChange={handleFileName}
                                                    fullWidth
                                                    required
                                                    variant="outlined"
                                                    error={isError('name')}
                                                    helperText={getErrorMessage('name')}
                                                />
                                            ) : (
                                                <span>none</span>
                                            )}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <FormHelperText error>{getErrorMessage('file')}</FormHelperText>
                            </Fragment>
                        ) : (
                            <TextField
                                id="input-file-name"
                                label="File Name"
                                value={values.name}
                                onChange={handleFileName}
                                fullWidth
                                required
                                variant="outlined"
                                margin="normal"
                                error={isError('name')}
                                helperText={getErrorMessage('name')}
                            />
                        )}
                    </Fragment>
                }
                {(step === 3) &&
                    <div className={classes.spacing}>
                        <Grid container alignItems="center" justify="space-between" spacing={3} className={classes.overflowFix}>
                            <Grid item>
                                <Typography variant="h6">Upload Progress</Typography>
                            </Grid>
                            <Grid item>
                                <CircularProgress variant="determinate" value={uploadProgress} />
                            </Grid>
                        </Grid>
                    </div>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    onClick={values.id == 0 ? uploadFile : postFile}
                    color="primary"
                    variant="contained"
                    disabled={! canPostFile()}
                    >
                    {values.id == 0 ? "Upload" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
